import {
  V3ApiResponse,
  PaginationParams,
  PaginatedResponse,
  buildPaginationQuery,
  CreateBasketRequest,
  UpdateBasketRequest,
  PatchBasketRequest,
  BasketCompositionRequest,
  BasketDetail,
  BasketListItem,
  BasketContractState,
  BasketSnapshotItem,
  PublishBasketResult,
  VersionListItem,
  VersionCommentMeta,
  VersionMetadataItem,
  CreateVersionMetadataRequest,
  AuditLogEntry,
  PortfolioUsageItem,
  ProductSearchItem,
  BasketActiveComposition,
  BasketAfterActivationResult,
  BasketCompositionChangeEntry,
  PaginatedCompositionChanges,
} from "../types/productV3";
import {
  PendingContractsData,
  BasketPendingContract,
  CancelPendingActivationsResult,
  LockStatusResult,
} from "../types/pendingContracts";
import httpService, { HttpError } from "./httpService";

const BASE_PATH = "/v3/products/baskets";
const SEARCH_PATH = "/v3/products/search/provider";

class BasketsV3Service {
  async createBasket(data: CreateBasketRequest): Promise<BasketDetail> {
    try {
      const response = await httpService.post<V3ApiResponse<BasketDetail>>(
        BASE_PATH,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 create basket error:", error);
      throw error;
    }
  }

  async updateBasket(
    basketId: number,
    data: UpdateBasketRequest
  ): Promise<BasketDetail> {
    try {
      const response = await httpService.put<V3ApiResponse<BasketDetail>>(
        `${BASE_PATH}/${basketId}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 update basket error:", error);
      throw error;
    }
  }

  async patchBasket(
    basketId: number,
    data: PatchBasketRequest
  ): Promise<BasketDetail> {
    try {
      const response = await httpService.patch<V3ApiResponse<BasketDetail>>(
        `${BASE_PATH}/${basketId}`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 patch basket error:", error);
      throw error;
    }
  }

  async updateComposition(
    basketId: number,
    data: BasketCompositionRequest
  ): Promise<BasketDetail> {
    try {
      const response = await httpService.put<V3ApiResponse<BasketDetail>>(
        `${BASE_PATH}/${basketId}/composition`,
        data
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 update basket composition error:", error);
      throw error;
    }
  }

  async publishBasket(basketId: number): Promise<PublishBasketResult> {
    try {
      const response = await httpService.post<
        V3ApiResponse<PublishBasketResult>
      >(`${BASE_PATH}/${basketId}/publish`);
      return response.data.data;
    } catch (error) {
      console.error("V3 publish basket error:", error);
      throw error;
    }
  }

  async getBasket(basketId: number): Promise<BasketDetail> {
    try {
      const response = await httpService.get<V3ApiResponse<BasketDetail>>(
        `${BASE_PATH}/${basketId}`
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 get basket error:", error);
      throw error;
    }
  }

  async getBasketSnapshots(
    basketId: number,
    params?: PaginationParams & { since?: string }
  ): Promise<PaginatedResponse<BasketSnapshotItem>> {
    try {
      const query = buildPaginationQuery(params);
      const sinceParam = params?.since ? `${query ? "&" : "?"}since=${encodeURIComponent(params.since)}` : "";
      const response = await httpService.get<
        PaginatedResponse<BasketSnapshotItem>
      >(`${BASE_PATH}/${basketId}/snapshots${query}${sinceParam}`);
      return response.data;
    } catch (error) {
      console.error("V3 get basket snapshots error:", error);
      throw error;
    }
  }

  async getContractStates(
    basketId: number
  ): Promise<BasketContractState[]> {
    try {
      const response = await httpService.get<
        V3ApiResponse<BasketContractState[]>
      >(`${BASE_PATH}/${basketId}/contract-states`);
      return response.data.data;
    } catch (error) {
      console.error("V3 get basket contract states error:", error);
      throw error;
    }
  }

  async getVersions(basketId: number): Promise<VersionListItem[]> {
    try {
      const response = await httpService.get<
        V3ApiResponse<VersionListItem[]>
      >(`${BASE_PATH}/${basketId}/versions`);
      return response.data.data;
    } catch (error) {
      console.error("V3 get basket versions error:", error);
      throw error;
    }
  }

  async getBaskets(
    params?: PaginationParams
  ): Promise<PaginatedResponse<BasketListItem>> {
    try {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<
        PaginatedResponse<BasketListItem>
      >(`${BASE_PATH}${query}`);
      return response.data;
    } catch (error) {
      console.error("V3 get baskets error:", error);
      throw error;
    }
  }

  async deleteBasket(basketId: number): Promise<void> {
    try {
      await httpService.delete(`${BASE_PATH}/${basketId}`);
    } catch (error) {
      console.error("V3 delete basket error:", error);
      throw error;
    }
  }

  async getAuditLog(
    basketId: number,
    params?: PaginationParams
  ): Promise<PaginatedResponse<AuditLogEntry>> {
    try {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<
        V3ApiResponse<PaginatedResponse<AuditLogEntry>>
      >(`${BASE_PATH}/${basketId}/user-updates${query}`);
      return response.data.data;
    } catch (error) {
      console.error("V3 get basket audit log error:", error);
      throw error;
    }
  }

  /**
   * Returns portfolios whose latest published version includes this basket,
   * with allocation details. Falls back to null if endpoint unavailable.
   * PWA-1378: GET /v3/products/baskets/{basketId}/portfolio-usage
   */
  async getPortfolioUsage(
    basketId: number
  ): Promise<PortfolioUsageItem[] | null> {
    try {
      const response = await httpService.get<
        V3ApiResponse<PortfolioUsageItem[]>
      >(`${BASE_PATH}/${basketId}/portfolio-usage`);
      return response.data.data;
    } catch (error) {
      if ((error as HttpError).status === 404) return null;
      throw error;
    }
  }
  async getActiveSnapshot(
    basketId: number
  ): Promise<ProductSearchItem | null> {
    try {
      const response = await httpService.get<
        PaginatedResponse<ProductSearchItem>
      >(`${SEARCH_PATH}?productType=BASKET&productId=${basketId}&pageSize=1`);
      const items = response.data.items ?? [];
      return items.length > 0 ? items[0] : null;
    } catch (error) {
      console.error("V3 get basket active snapshot error:", error);
      return null;
    }
  }
  async getPendingContracts(
    basketId: number
  ): Promise<PendingContractsData<BasketPendingContract>> {
    try {
      const response = await httpService.get<
        V3ApiResponse<PendingContractsData<BasketPendingContract>>
      >(`${BASE_PATH}/${basketId}/pending-contracts`);
      return response.data.data;
    } catch (error) {
      console.error("V3 get basket pending contracts error:", error);
      throw error;
    }
  }

  async cancelPendingActivations(
    basketId: number
  ): Promise<CancelPendingActivationsResult> {
    try {
      const response = await httpService.post<
        V3ApiResponse<CancelPendingActivationsResult>
      >(`${BASE_PATH}/${basketId}/cancel-pending-activations`);
      return response.data.data;
    } catch (error) {
      console.error("V3 cancel basket pending activations error:", error);
      throw error;
    }
  }

  async getLockStatus(basketId: number): Promise<LockStatusResult> {
    try {
      const response = await httpService.get<LockStatusResult>(
        `${BASE_PATH}/${basketId}/lock-status`
      );
      return response.data;
    } catch (error) {
      console.error("V3 get basket lock status error:", error);
      throw error;
    }
  }

  async getActiveComposition(basketId: number): Promise<BasketActiveComposition> {
    try {
      const response = await httpService.get<V3ApiResponse<BasketActiveComposition>>(
        `${BASE_PATH}/${basketId}/active`
      );
      return response.data.data;
    } catch (error) {
      // Retry once
      try {
        const response = await httpService.get<V3ApiResponse<BasketActiveComposition>>(
          `${BASE_PATH}/${basketId}/active`
        );
        return response.data.data;
      } catch (retryError) {
        console.error("V3 get basket active composition error:", retryError);
        throw retryError;
      }
    }
  }

  async getAfterActivation(basketId: number): Promise<BasketAfterActivationResult> {
    try {
      const response = await httpService.get<V3ApiResponse<BasketAfterActivationResult>>(
        `${BASE_PATH}/${basketId}/after-activation`
      );
      return response.data.data;
    } catch (error) {
      // Retry once
      try {
        const response = await httpService.get<V3ApiResponse<BasketAfterActivationResult>>(
          `${BASE_PATH}/${basketId}/after-activation`
        );
        return response.data.data;
      } catch (retryError) {
        console.error("V3 get basket after-activation error:", retryError);
        throw retryError;
      }
    }
  }

  async getChanges(basketId: number): Promise<PaginatedCompositionChanges<BasketCompositionChangeEntry>> {
    try {
      const response = await httpService.get<V3ApiResponse<PaginatedCompositionChanges<BasketCompositionChangeEntry>>>(
        `${BASE_PATH}/${basketId}/changes`
      );
      return response.data.data;
    } catch (error) {
      // Retry once
      try {
        const response = await httpService.get<V3ApiResponse<PaginatedCompositionChanges<BasketCompositionChangeEntry>>>(
          `${BASE_PATH}/${basketId}/changes`
        );
        return response.data.data;
      } catch (retryError) {
        console.error("V3 get basket changes error:", retryError);
        throw retryError;
      }
    }
  }

  // --- Version metadata (comments) — PWA-1666 ---

  async createVersionMetadata(
    basketId: number,
    versionId: number,
    metadata: VersionCommentMeta
  ): Promise<VersionMetadataItem> {
    try {
      const body: CreateVersionMetadataRequest = { metadata };
      const response = await httpService.post<V3ApiResponse<VersionMetadataItem>>(
        `${BASE_PATH}/${basketId}/versions/${versionId}/metadata`,
        body
      );
      return response.data.data;
    } catch (error) {
      console.error("V3 create basket version metadata error:", error);
      throw error;
    }
  }

  async getVersionMetadata(
    basketId: number,
    versionId: number,
    params?: PaginationParams
  ): Promise<PaginatedResponse<VersionMetadataItem>> {
    try {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<PaginatedResponse<VersionMetadataItem>>(
        `${BASE_PATH}/${basketId}/versions/${versionId}/metadata${query}`
      );
      return response.data;
    } catch (error) {
      console.error("V3 get basket version metadata error:", error);
      throw error;
    }
  }

  async deleteVersionMetadata(
    basketId: number,
    versionId: number,
    metadataId: number
  ): Promise<void> {
    try {
      await httpService.delete(
        `${BASE_PATH}/${basketId}/versions/${versionId}/metadata/${metadataId}`
      );
    } catch (error) {
      console.error("V3 delete basket version metadata error:", error);
      throw error;
    }
  }

  async getAllVersionMetadata(
    basketId: number,
    params?: PaginationParams
  ): Promise<PaginatedResponse<VersionMetadataItem>> {
    try {
      const query = buildPaginationQuery(params);
      const response = await httpService.get<PaginatedResponse<VersionMetadataItem>>(
        `${BASE_PATH}/${basketId}/version-metadata${query}`
      );
      return response.data;
    } catch (error) {
      console.error("V3 get all basket version metadata error:", error);
      throw error;
    }
  }
}

export const basketsV3Service = new BasketsV3Service();
